import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router";
import { ONBOARDING_COPY, STARTER_TEMPLATES } from "@hicya/habits-shared/data";
import { isDue, parseScheduleConfig, getEffectiveDate } from "@hicya/habits-shared/engine";

import { HabitIcon } from "@/components/ui/habit-icon";
import { defaultHabitColor } from "@/components/ui/habit-icon-map";
import { useHabits } from "@/context/habits-context";
import { useDatabase } from "@/db/provider";

import styles from "./onboarding.module.scss";

import type { HabitTemplate } from "@hicya/habits-shared/data";

type OnboardingStep = 1 | 2 | 3;

interface CreatedHabitInfo {
  id: string;
  type: "boolean" | "count";
  scheduleType: string;
  scheduleValue: string;
}

export function Onboarding() {
  const navigate = useNavigate();
  const { createHabit, completeHabit, updateSettings } = useHabits();
  const db = useDatabase();
  const [step, setStep] = useState<OnboardingStep>(1);
  const [selectedId, setSelectedId] = useState("t-water");
  const [error, setError] = useState("");
  const busy = useRef(false);
  const stepHeading = useRef<HTMLHeadingElement>(null);
  const selected = STARTER_TEMPLATES.find((t) => t.id === selectedId) ?? STARTER_TEMPLATES[0];
  const starterLabels: Record<string, string> = {
    "t-water": "water",
    "t-read": "reading",
    "t-workout": "a workout",
    "t-meditate": "meditation",
    "t-sleep-early": "sleep",
    "t-journal": "journaling",
  };
  useEffect(() => {
    if (step > 1) stepHeading.current?.focus();
  }, [step]);
  const [creating, setCreating] = useState(false);
  const [createdHabit, setCreatedHabit] = useState<CreatedHabitInfo | null>(null);
  const [encouragement, setEncouragement] = useState(false);

  const finishOnboarding = useCallback(async () => {
    await updateSettings("onboardingCompleted", "true");
    setEncouragement(true);
    setTimeout(() => setEncouragement(false), 3000);
  }, [updateSettings]);

  const isHabitActionableToday = useCallback((info: CreatedHabitInfo): boolean => {
    if (info.type !== "boolean") return false;

    const config = parseScheduleConfig(
      info.scheduleType as Parameters<typeof parseScheduleConfig>[0],
      info.scheduleValue,
    );

    if (config.type === "specific_days") {
      const stored = localStorage.getItem("habits:dayBoundaryHour");
      const boundaryHour =
        stored !== null ? Math.max(0, Math.min(23, parseInt(stored, 10) || 0)) : 0;
      const today = getEffectiveDate(
        new Date().toISOString(),
        Intl.DateTimeFormat().resolvedOptions().timeZone,
        boundaryHour,
      );
      return isDue(config, today, { habitStartDate: today, completions: [] });
    }

    return true;
  }, []);

  const handleSelectTemplate = useCallback(
    async (templateId: string) => {
      if (busy.current) return;
      busy.current = true;
      setCreating(true);
      setError("");

      const template = STARTER_TEMPLATES.find((t: HabitTemplate) => t.id === templateId);
      if (!template) {
        busy.current = false;
        setCreating(false);
        return;
      }

      try {
        const habitCount = await db.getHabitCount();
        const habitId = crypto.randomUUID();

        await createHabit({
          id: habitId,
          name: template.name,
          type: template.type,
          schedule_type: template.scheduleType,
          schedule_value: template.scheduleValue,
          time_of_day: template.timeOfDay,
          target_count: template.targetCount,
          color: defaultHabitColor(template.icon),
          icon: template.icon,
          category_id: null,
          sort_order: Number(habitCount),
          day_boundary_hour: 0,
          show_streak: true,
          archived_at: null,
          deleted_at: null,
        });

        setCreatedHabit({
          id: habitId,
          type: template.type,
          scheduleType: template.scheduleType,
          scheduleValue: template.scheduleValue,
        });
        setStep(2);
      } catch {
        setError("Couldn’t save your habit. Please try again.");
      } finally {
        busy.current = false;
        setCreating(false);
      }
    },
    [createHabit, db],
  );

  const handleCreateOwn = useCallback(async () => {
    await updateSettings("onboardingCompleted", "true");
    void navigate("/habits/new");
  }, [updateSettings, navigate]);

  const handleStep2Continue = useCallback(() => {
    if (createdHabit && isHabitActionableToday(createdHabit)) {
      setStep(3);
    } else {
      void finishOnboarding();
    }
  }, [createdHabit, isHabitActionableToday, finishOnboarding]);

  const handleStep3Complete = useCallback(async () => {
    if (createdHabit) {
      await completeHabit(createdHabit.id, crypto.randomUUID());
    }
    await finishOnboarding();
  }, [createdHabit, completeHabit, finishOnboarding]);

  if (encouragement) {
    return (
      <div className={styles.encouragement}>
        <p className={styles.encouragementText}>{ONBOARDING_COPY.postCreation}</p>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {step > 1 && <StepIndicator current={step} total={3} />}

      {step === 1 && (
        <div className={styles.home}>
          <div className={styles.hero}>
            <div className={styles.welcome}>
              <h2 className={styles.heroTitle}>
                Start with one
                <br />
                small habit.
              </h2>
              <p className={styles.heroQuestion}>What would you like to make time for?</p>
            </div>
            <div className={styles.illustration} aria-hidden="true">
              <img
                className={styles.lightArt}
                src="/art/hero-light.webp"
                width={800}
                height={800}
                alt=""
                fetchPriority="high"
              />
              <img
                className={styles.darkArt}
                src="/art/hero-dark.webp"
                width={800}
                height={800}
                alt=""
              />
            </div>
          </div>
          <div className={styles.starters} role="group" aria-label="Choose your first habit">
            {STARTER_TEMPLATES.map((template: HabitTemplate) => (
              <button
                key={template.id}
                type="button"
                className={styles.starter}
                onClick={() => setSelectedId(template.id)}
                aria-pressed={selectedId === template.id}
                disabled={creating}
                data-testid={`onboarding-template-${template.id}`}
              >
                <span className={styles.starterArt}>
                  <HabitIcon value={template.icon} size={112} />
                  {selectedId === template.id && (
                    <span className={styles.selectedCheck}>
                      <CheckIcon size={16} weight="bold" />
                    </span>
                  )}
                </span>
                <span>{template.name}</span>
              </button>
            ))}
          </div>
          <div className={styles.startActions}>
            <button
              type="button"
              className={styles.primaryButton}
              disabled={creating}
              onClick={() => void handleSelectTemplate(selected.id)}
              data-testid="onboarding-start"
            >
              {creating
                ? "Saving…"
                : `Start with ${starterLabels[selected.id] ?? selected.name.toLowerCase()}`}{" "}
              <ArrowRightIcon size={24} />
            </button>
            {error && (
              <p className={styles.error} role="alert">
                {error}
              </p>
            )}
            <button
              type="button"
              className={styles.createOwn}
              onClick={() => void handleCreateOwn()}
              disabled={creating}
              data-testid="onboarding-create-own"
            >
              Create your own
            </button>
          </div>
        </div>
      )}

      {step === 2 && (
        <div className={styles.stepContent}>
          <div className={styles.welcome}>
            <h2 className={styles.title} ref={stepHeading} tabIndex={-1}>
              {ONBOARDING_COPY.step2Title}
            </h2>
            <p className={styles.subtitle}>{ONBOARDING_COPY.step2Subtitle}</p>
          </div>

          <p className={styles.scorePreview}>{ONBOARDING_COPY.step2ScorePreview}</p>

          <button
            type="button"
            className={styles.privacyLink}
            onClick={() => void navigate("/settings")}
          >
            {ONBOARDING_COPY.step2PrivacyLink}
          </button>

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleStep2Continue}
              data-testid="onboarding-step2-continue"
            >
              Continue
            </button>
            <button
              type="button"
              className={styles.skipLink}
              onClick={() => void finishOnboarding()}
              data-testid="onboarding-step2-skip"
            >
              Skip
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={styles.stepContent}>
          <div className={styles.welcome}>
            <h2 className={styles.title} ref={stepHeading} tabIndex={-1}>
              {ONBOARDING_COPY.step3Title}
            </h2>
            <p className={styles.subtitle}>{ONBOARDING_COPY.step3Subtitle}</p>
          </div>

          <div className={styles.stepActions}>
            <button
              type="button"
              className={styles.doneButton}
              onClick={() => void handleStep3Complete()}
              data-testid="onboarding-step3-done"
            >
              {ONBOARDING_COPY.step3Done}
            </button>
            <button
              type="button"
              className={styles.skipLink}
              onClick={() => void finishOnboarding()}
              data-testid="onboarding-step3-skip"
            >
              {ONBOARDING_COPY.step3Skip}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function StepIndicator({ current, total }: { current: number; total: number }) {
  return (
    <div className={styles.stepIndicator}>
      {Array.from({ length: total }, (_, i) => (
        <span
          key={i}
          className={`${styles.dot} ${i + 1 === current ? styles.dotActive : ""} ${i + 1 < current ? styles.dotCompleted : ""}`}
        />
      ))}
    </div>
  );
}
